import { runGame } from "@shared/actions/games";
import { checkUser, signOut } from "@shared/actions/user";
import { IFrameError } from "@shared/components";
import { useCurrentTagId, useGameErrorStatus } from "@shared/hooks/games";
import { useBrands } from "@shared/hooks/games/brands";
import { useProviders } from "@shared/hooks/games/game-providers";
import {
  useAspectRatio,
  useGameRun,
  useGames,
  useGameSession,
  useGamesFetch,
} from "@shared/hooks/games/games";
import { useIsAuth, useUserState } from "@shared/hooks/user";
import { useRegisterUpdateGameBalanceService } from "@shared/hooks/uses-services";
import {
  BuyInternetTimeModal,
  BuyInternetTimeResponseModal,
} from "@shared/modules/sweepstakes";
import { gameFix } from "@shared/utils/game-fix";
import { getGameRunType } from "@shared/utils/run-type";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Redirect, useParams } from "react-router-dom";

import GameMessage from "../../components/Game/GameMessage";
import GameModal from "../../components/Modal/GameModal";

export const GamesPage = () => {
  const { stringId } = useParams<{ stringId: string }>() ?? {};
  const currentTagId = useCurrentTagId();
  const dispatch = useDispatch();
  const userState = useUserState() ?? {};
  const gameRun = useGameRun();
  const gamesFetch = useGamesFetch();
  const gameSession = useGameSession();
  const games = useGames();
  const brands = useBrands();
  const providers = useProviders();
  const { message } = useGameErrorStatus();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const GameMessageComponent = GameMessage as any;
  const isAuth = useIsAuth();

  useRegisterUpdateGameBalanceService({
    registerCallback: () => dispatch(checkUser()),
  });

  const game = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => games?.find((game: any) => game.string_id === stringId),
    [stringId, games]
  );

  const brand = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => brands?.find((brand: any) => brand.id === game?.game_section_id),
    [brands, game?.game_section_id]
  );

  const provider = useMemo(
    () => providers?.find((provider) => provider.id === game?.game_provider_id),
    [providers, game?.game_provider_id]
  );

  const aspectRatio = useAspectRatio(brand, game);

  useEffect(() => {
    if (stringId) {
      dispatch(
        runGame({
          game: stringId,
          demo: false,
          place: getGameRunType(currentTagId),
        })
      );
    }
  }, [currentTagId, dispatch, stringId]);

  if (game && brand && provider) {
    gameFix.loadGameData(game, brand, provider);
    gameFix.analyzeGame(false);
  }

  if (!isAuth) {
    return <Redirect to="" />;
  }

  if (
    gameRun === "rejected" &&
    gamesFetch === "fulfilled" &&
    message?.includes("Game is prohibited")
  ) {
    return <GameMessageComponent translates={["error"]} isShowLink={true} />;
  }

  if (gameFix.isGameFrameError && gameSession) {
    return (
      <GameMessageComponent translates={["iframeErr"]} isOnTop={true}>
        <IFrameError url={gameSession} />
      </GameMessageComponent>
    );
  }

  return (
    <>
      <BuyInternetTimeModal />
      <BuyInternetTimeResponseModal />
      <GameModal
        auth={userState}
        exit={() => dispatch(signOut())}
        aspectRatio={aspectRatio}
        session={gameSession}
        frameSettings={gameFix.frameSettings()}
        {...game}
      />
    </>
  );
};
